import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { Team } from '@yandex-int/k-common/typings';
import { SimpleCallback, SimpleText } from 'platform-components/common';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';

import { getCurrentClass, getCurrentTeamPrevLevel } from 'common.components/user/selectors';
import { getTrimmedName } from 'utils/get-trimmed-name';
import { getTeamIdFromPath } from 'utils/path';

import { NewYearModalControl } from '../__control/new-year-modal__control';
import { NEW_YEAR_MODAL_CONTROL_VIEW } from '../__control/types';
import { NewYearModalControls } from '../__controls/new-year-modal__controls';
import { NewYearModalDescription } from '../__description/new-year-modal__description';
import { NewYearModalHeader } from '../__header/new-year-modal__header';
import { NewYearModalMainContent } from '../__main-content/new-year-modal__main-content';
import { RootAction, ReduxTeacherStore } from '../../../typings';
import * as actions from '../actions';
import { MAX_CLASS_NAME_LENGTH, PAGES } from '../new-year-modal.constants';
import { getLoadingStatus, getShouldArchiveClass } from '../selectors';

import './new-year-modal__content_archive.scss';

interface StoreProps {
    currentClass: Team | null;
    prevLevel: number | null;
    shouldArchiveClass: boolean;
    loadingStatus: LOADING_STATUSES;
}

interface DispatchProps {
    setPage: typeof actions.setPage;
}

interface Props extends StoreProps, DispatchProps {
    onActualize: SimpleCallback;
    onArchive: SimpleCallback;
}

export const NewYearModalContentArchive = ({
    currentClass,
    prevLevel,
    loadingStatus,
    onArchive,
    onActualize,
    setPage,
    shouldArchiveClass,
}: Props) => {
    const isLoading = loadingStatus === LOADING_STATUSES.LOADING;

    const toRenameTeam = () => {
        setPage(PAGES.RENAME);
    };

    if (!currentClass) {
        return null;
    }

    const { name: teamName } = currentClass;
    const decline = shouldArchiveClass
        ? {
              captionId: 'newYearModal.form.continueWorking',
              onClick: onActualize,
          }
        : {
              captionId: 'newYearModal.form.back',
              onClick: toRenameTeam,
          };
    const isSchoolTeam = prevLevel && prevLevel < 11;
    return (
        <>
            <NewYearModalHeader
                id={isSchoolTeam ? 'newYearModal.archive.header' : 'newYearModal.archive.header.group'}
                values={{
                    className: getTrimmedName(teamName, MAX_CLASS_NAME_LENGTH),
                    level: isSchoolTeam ? prevLevel : null,
                }}
            />
            <NewYearModalMainContent mix="new-year-modal__content_archive">
                <NewYearModalDescription id="newYearModal.archive2.description1" />
                <NewYearModalDescription id="newYearModal.archive2.description2" />
            </NewYearModalMainContent>
            <NewYearModalControls stackable={shouldArchiveClass}>
                <NewYearModalControl
                    disabled={isLoading}
                    onClick={decline.onClick}
                    view={NEW_YEAR_MODAL_CONTROL_VIEW.DECLINE}
                >
                    <SimpleText id={decline.captionId} />
                </NewYearModalControl>
                <NewYearModalControl onClick={onArchive} progress={isLoading} view={NEW_YEAR_MODAL_CONTROL_VIEW.ACCEPT}>
                    <SimpleText id="newYearModal.form2.archiveClass" />
                </NewYearModalControl>
            </NewYearModalControls>
        </>
    );
};

const mapStateToProps = (storeState: ReduxTeacherStore): StoreProps => {
    const classId = getTeamIdFromPath();

    return {
        currentClass: getCurrentClass(storeState, { classId }),
        shouldArchiveClass: getShouldArchiveClass(storeState, { classId }),
        loadingStatus: getLoadingStatus(storeState),
        prevLevel: getCurrentTeamPrevLevel(storeState, { classId }),
    };
};

NewYearModalContentArchive.contextTypes = {
    staticUrls: PropTypes.shape({
        labClassStudents: PropTypes.string.isRequired,
        labClasses: PropTypes.string.isRequired,
    }),
};

const mapDispatchToProps = (dispatch: Dispatch<RootAction>): DispatchProps => {
    const { setPage } = actions;

    return bindActionCreators(
        {
            setPage,
        },
        dispatch
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(NewYearModalContentArchive);
